import { v4 as uuidv4 } from 'uuid';

// Layout type constants
const LAYOUT_TYPES = {
    DEFAULT: 'default',
    STARTER: 'starter',
    STATISTICS: 'statistics',
    TIMELINE: 'timeline',
    UNIT_LIVE: 'unit-live',
    UNIT_RECORDING: 'unit-recording',
    UNIT_CAMERAS: 'unit-cameras',
    ALARM_DMP_LOGS: 'alarm-dmp-logs',
    MAINTENANCE_VIEW: 'maintenance-view'
};

const VIDEO_LAYOUT_THRESHOLD = 4;

// Grid geometry shared with MainLayout's GridStack init (column: 12, maxRow: 8).
const GRID_COLUMNS = 12;
const GRID_MAX_ROWS = 8;

// Axis-aligned rectangle overlap test in grid units.
function rectsOverlap(a, b) {
    return a.x < b.x + b.w && a.x + a.w > b.x &&
           a.y < b.y + b.h && a.y + a.h > b.y;
}

// First free top-left spot for `node`, scanning row by row; null if none fits.
function findFreeSpot(node, occupied, columns, maxRows) {
    for (let y = 0; y <= maxRows - node.h; y++) {
        for (let x = 0; x <= columns - node.w; x++) {
            const candidate = { x, y, w: node.w, h: node.h };
            if (!occupied.some(rect => rectsOverlap(candidate, rect))) {
                return { x, y };
            }
        }
    }
    return null;
}

/**
 * De-overlaps grid items before they reach GridStack. Two widgets sharing a
 * cell make the engine's _fixCollisions ↔ moveNode recurse into a stack
 * overflow when the layout fills maxRow with no free row to push the loser
 * into. Clamps to bounds, keeps non-overlapping items, relocates the rest to
 * the first free spot. Clean layouts pass through unchanged.
 *
 * @param {Array} items - grid items to sanitize (not mutated)
 * @returns {Array} new array, overlap-free, original order kept
 */
export function sanitizeGridItems(items = [], { columns = GRID_COLUMNS, maxRows = GRID_MAX_ROWS } = {}) {
    if (!Array.isArray(items) || items.length === 0) return items;

    const occupied = [];
    const fixedByIndex = {};

    // Place in reading order so the de-overlap is stable regardless of array order.
    items
        .map((item, index) => ({ item, index }))
        .sort((a, b) =>
            ((a.item.y || 0) - (b.item.y || 0)) ||
            ((a.item.x || 0) - (b.item.x || 0)) ||
            (a.index - b.index)
        )
        .forEach(({ item, index }) => {
            const node = { ...item };

            // Clamp size and position inside the grid first.
            node.w = Math.max(1, Math.min(Number(node.w) || 1, columns));
            node.h = Math.max(1, Math.min(Number(node.h) || 1, maxRows));
            node.x = Math.max(0, Math.min(Number(node.x) || 0, columns - node.w));
            node.y = Math.max(0, Number(node.y) || 0);

            const overflowsMaxRows = node.y + node.h > maxRows;

            if (overflowsMaxRows || occupied.some(rect => rectsOverlap(node, rect))) {
                const spot = findFreeSpot(node, occupied, columns, maxRows);
                if (spot) {
                    node.x = spot.x;
                    node.y = spot.y;
                } else {
                    // Nothing fits within maxRows — stack below everything else.
                    node.y = occupied.reduce((max, rect) => Math.max(max, rect.y + rect.h), 0);
                    node.x = 0;
                }
            }

            occupied.push({ x: node.x, y: node.y, w: node.w, h: node.h });
            fixedByIndex[index] = node;
        });

    return items.map((_, index) => fixedByIndex[index]);
}

/**
 * Safely deep clones an object
 * @param {Object} obj - Object to clone
 * @returns {Object} - Deep copy of the object
 */
function safeDeepClone(obj) {
    return JSON.parse(JSON.stringify(obj || {}));
}

/**
 * Assigns fresh unique ids to a set of grid items so that two tabs never share
 * the same widget id.
 *
 * @param {Array} gridItems - grid items to re-id (returns a new array)
 * @param {Array} [slots] - optional slots whose ids should follow the mapping
 * @returns {Array} - grid items with fresh, unique ids
 */
export function regenerateLayoutIds(gridItems = [], slots = null) {
    const idMap = {};

    const newGridItems = (gridItems || []).map(item => {
        const newId = uuidv4();
        if (item && item.id != null) idMap[item.id] = newId;
        return { ...item, id: newId };
    });

    if (Array.isArray(slots)) {
        slots.forEach(slot => {
            if (slot && slot.id != null && idMap[slot.id]) {
                slot.id = idMap[slot.id];
            }
        });
    }

    return newGridItems;
}

/**
 * Loads the default layout configuration
 * @returns {Array} - Default layout grid items
 */
function loadDefaultLayoutConfig() {
    const defaultConfig = require('@/config/defaultLayout').default;
    return safeDeepClone(defaultConfig.grid || []);
}

/**
 * Loads the starter layout configuration
 * @returns {Array} - Start layout grid items
 */
function loadStarterLayoutConfig() {
  const starterConfig = require('@/config/starterLayout').default;
  return safeDeepClone(starterConfig.grid || []);
}

/**
 * Loads the statistics layout configuration
 * @returns {Array} - Statistics layout grid items
 */
function loadStatisticsLayoutConfig() {
    const statisticsConfig = require('@/config/statisticsLayout').default;
    return safeDeepClone(statisticsConfig.grid || []);
}

/**
 * Loads the timeline layout configuration
 * @returns {Array} - Timeline layout grid items
 */
function loadTimelineLayoutConfig() {
    const timelineConfig = require('@/config/timelineLayout').default;
    return safeDeepClone(timelineConfig.grid || []);
}

/**
 * Loads the video layout configuration based on slot count
 * @param {number} slotCount - Number of available slots
 * @returns {Array} - Video layout grid items
 */
function loadVideoLayoutConfig(slotCount) {
    const config = slotCount > VIDEO_LAYOUT_THRESHOLD
        ? require('@/config/liveVideo5x1Layout').default
        : require('@/config/liveVideoLayout').default;
    return safeDeepClone(config.grid || []);
}

export function buildSingleSlotGridItems(idPrefix = 'slot') {
    return [{
        x: 0,
        y: 0,
        w: GRID_COLUMNS,
        h: GRID_MAX_ROWS,
        minW: 2,
        minH: 2,
        id: `${idPrefix}-1`,
        content: `${GRID_COLUMNS} x ${GRID_MAX_ROWS}`,
        name: null,
        type: null,
        pos: 1,
        data: null,
    }];
}

export function buildUnitCamerasGridItems(slotCount) {
    const count = Math.min(Math.max(1, slotCount || 0), 24);

    const cols = Math.min(6, Math.max(Math.ceil(Math.sqrt(count)), Math.ceil(count / 4)));
    const rows = Math.ceil(count / cols);
    const w = Math.max(2, Math.floor(GRID_COLUMNS / cols));
    const h = Math.max(2, Math.floor(GRID_MAX_ROWS / rows));

    const items = [];
    for (let i = 0; i < count; i++) {
        items.push({
            x: (i % cols) * w,
            y: Math.floor(i / cols) * h,
            w,
            h,
            minW: Math.min(2, w),
            minH: Math.min(2, h),
            id: `unit-cameras-${i + 1}`,
            content: `${w} x ${h}`,
            name: null,
            type: null,
            pos: i + 1,
            data: null,
        });
    }
    return items;
}

/**
 * Loads user-defined layout configuration with fallback
 * @param {String|Number} layoutId - Layout ID
 * @param {Object} rootGetters - Vuex root getters
 * @returns {Array} - User layout grid items or default fallback
 */
function loadUserLayoutConfigWithFallback(layoutId, rootGetters) {
    const layoutData = rootGetters["layouts/getUserLayouts"].find(l => l.id === layoutId);

    if (layoutData && layoutData.grid) {
        return safeDeepClone(layoutData.grid);
    }

    // Fallback to default layout if user layout not found
    return loadDefaultLayoutConfig();
}

/**
 * Loads the maintenance layout configuration
 * @returns {Array} - Timeline layout grid items
 */
function loadMaintenanceLayoutConfig() {
    const maintenanceConfig = require('@/config/maintenanceLayout').default;
    return safeDeepClone(maintenanceConfig.grid || []);
}

/**
 * Gets default layout as fallback for unhandled cases
 * @returns {Array} - Default layout grid items
 */
function getDefaultLayoutFallback() {
    return loadDefaultLayoutConfig();
}

/**
 * Helps to get the grid items to build the layout with gridstack
 *
 * @param {String|Number} layoutId - ID of the layout to load
 * @param {Object} options - Additional options (slots, rootGetters)
 * @returns {Array} - Array of grid items
 */
export function getGridItemsForLayout(layoutId, options = {}) {
    // Sanitize so overlapping saved/user layouts can't crash GridStack on mount.
    return sanitizeGridItems(resolveGridItemsForLayout(layoutId, options));
}

// Resolves the raw grid items for a layout id (no overlap sanitation).
function resolveGridItemsForLayout(layoutId, options = {}) {
    const {slots = [], rootGetters = null} = options;

    // Handle numeric default layout ID
    if (layoutId === LAYOUT_TYPES.DEFAULT || layoutId === 1) {
        return loadDefaultLayoutConfig();
    }

    switch (layoutId) {
        case LAYOUT_TYPES.STATISTICS:
            return loadStatisticsLayoutConfig();

        case LAYOUT_TYPES.TIMELINE:
            return loadTimelineLayoutConfig();

        case LAYOUT_TYPES.STARTER:
          return loadStarterLayoutConfig();

        case LAYOUT_TYPES.MAINTENANCE_VIEW:
          return loadMaintenanceLayoutConfig();

        case LAYOUT_TYPES.UNIT_LIVE:
        case LAYOUT_TYPES.UNIT_RECORDING:
            return loadVideoLayoutConfig(slots?.length || 0);

        case LAYOUT_TYPES.UNIT_CAMERAS:
            return buildUnitCamerasGridItems(slots?.length || 0);

        case LAYOUT_TYPES.ALARM_DMP_LOGS:
            return buildSingleSlotGridItems('alarm-dmp-logs');

        default:
            if (layoutId && rootGetters) {
                return loadUserLayoutConfigWithFallback(parseInt(layoutId), rootGetters);
            }
            return getDefaultLayoutFallback();
    }
}

/**
 * Clears all widget properties from grid slots
 * @param {Array} grid - Grid to clear
 */
function clearGridSlots(grid) {
    grid.forEach(slot => {
        slot.type = null;
        slot.name = null;
        slot.data = null;
        slot.type2 = null;
    });
}

/**
 * Assigns widget properties to target slot
 * @param {Object} targetSlot - Target slot for widget assignment
 * @param {Object} widget - Widget to assign
 */
function assignWidgetToSlot(targetSlot, widget) {
    targetSlot.type = widget.type;
    targetSlot.name = widget.name;
    targetSlot.data = widget.data;
    targetSlot.type2 = widget.type2 || widget.type;
    targetSlot.widget_id = widget.id;
}

/**
 * Finds an available slot using cascading mapping strategy
 * @param {Array} grid - Grid to search in
 * @param {Object} widget - Widget to map
 * @returns {Object|undefined} - Found slot or undefined
 */
function findAvailableSlot(grid, widget) {
    // Priority 1: Same position
    let targetSlot = grid.find(slot => !slot.type && slot.pos === widget.pos);

    if (!targetSlot) {
        // Priority 2: Same x, y coordinates
        targetSlot = grid.find(slot =>
            !slot.type && slot.x === widget.x && slot.y === widget.y
        );
    }

    if (!targetSlot) {
        // Priority 3: Any available slot
        targetSlot = grid.find(slot => !slot.type);
    }

    return targetSlot;
}

/**
 * Maps the slots to the new layout prioritizing the position
 * @param {Array} currentSlots - Current slots with widgets
 * @param {Array} newLayoutGrid - New grid to map
 * @param {Array} hiddenSlots - Acts as cache
 * @returns {Object} - Object containing mappedSlots and unmappedWidgets
 */
export function mapSlotsToNewLayout(currentSlots, newLayoutGrid, hiddenSlots = []) {
    const newGrid = safeDeepClone(newLayoutGrid);
    const activeWidgets = [...currentSlots.filter(slot => slot.type), ...hiddenSlots];
    const unmappedWidgets = [];

    clearGridSlots(newGrid);

    activeWidgets.forEach(widget => {
        const targetSlot = findAvailableSlot(newGrid, widget);

        if (targetSlot) {
            assignWidgetToSlot(targetSlot, widget);
        } else {
            unmappedWidgets.push({...widget});
        }
    });

    return {
        mappedSlots: newGrid,
        unmappedWidgets
    };
}
